import React, { useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { BookOpen, Users, Lightbulb, Award } from 'lucide-react';
import { setPageMeta } from '../utils/seo';

/**
 * Curriculum Page
 * Main landing page for curriculum information with links to individual grades
 */
const Curriculum = () => {
  useEffect(() => {
    setPageMeta({
      title: 'Curriculum — Dual Language Elementary School in Harlem | PS 155 William Paca',
      description: 'Discover the Pre-K through Grade 5 curriculum at P.S. 155 William Paca, a dual language elementary school in Harlem offering Spanish immersion and rigorous academics.',
    });
  }, []);
  const grades = [
    {
      name: 'Pre-K',
      path: '/curriculum/pre-k',
      image: 'https://3.files.edl.io/66a7/25/03/10/142725-374b5192-9d08-4427-99d4-e68a5121c5d3.jpeg',
      description: 'Creative Play in Early Childhood',
      ageRange: 'Ages 3-4',
      color: 'from-pink-500 to-rose-500'
    },
    {
      name: 'Kindergarten',
      path: '/curriculum/kindergarten',
      image: 'https://3.files.edl.io/f1a0/25/03/06/153130-4ff331c1-ccf6-45c7-b911-3a80a604bb1c.jpg',
      description: 'Building Foundational Skills',
      ageRange: 'Ages 5-6',
      color: 'from-purple-500 to-violet-500'
    },
    {
      name: 'Grade 1',
      path: '/curriculum/grade-1',
      image: 'https://3.files.edl.io/70ca/25/02/12/212017-3bb265ce-9203-4065-a52b-7830547a6974.png',
      description: 'Developing Reading & Math Fluency',
      ageRange: 'Ages 6-7',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      name: 'Grade 2',
      path: '/curriculum/grade-2',
      image: 'https://3.files.edl.io/d6c4/25/02/12/212059-14c8bcd2-e48f-4d89-8ea5-84523aed39ae.png',
      description: 'Expanding Literacy & Numeracy',
      ageRange: 'Ages 7-8',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      name: 'Grade 3',
      path: '/curriculum/grade-3',
      image: 'https://3.files.edl.io/6f24/22/03/22/161414-a9b6960d-de73-4da2-864f-5230fbe390cd.JPG',
      description: 'Critical Thinking & Analysis',
      ageRange: 'Ages 8-9',
      color: 'from-amber-500 to-orange-500'
    },
    {
      name: 'Grade 4',
      path: '/curriculum/grade-4',
      image: 'https://3.files.edl.io/7162/25/02/12/213232-06190934-c537-4de1-b647-97e32c0cf0b5.jpg',
      description: 'Deep Dive into Complex Topics',
      ageRange: 'Ages 9-10',
      color: 'from-red-500 to-pink-500'
    },
    {
      name: 'Grade 5',
      path: '/curriculum/grade-5',
      image: 'https://3.files.edl.io/66a7/25/03/10/142725-374b5192-9d08-4427-99d4-e68a5121c5d3.jpeg',
      description: 'Preparing for Middle School',
      ageRange: 'Ages 10-11',
      color: 'from-indigo-500 to-purple-500'
    }
  ];

  const highlights = [
    {
      icon: BookOpen,
      title: 'Rigorous Standards',
      description: 'Aligned to NY Next Generation Learning Standards',
      color: 'text-school-blue',
      bgColor: 'bg-blue-50'
    },
    {
      icon: Users,
      title: 'Student-Centered',
      description: 'Instruction centers student voice and cultural responsiveness',
      color: 'text-school-gold-dark',
      bgColor: 'bg-amber-50'
    },
    {
      icon: Lightbulb,
      title: 'Joyful Learning',
      description: 'Engaging, hands-on experiences that inspire curiosity',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      icon: Award,
      title: 'Evidence-Based',
      description: 'Research-backed curricula from HMH and Eureka Math',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50 font-sans text-neutral-800 selection:bg-school-gold selection:text-white">
      <Navbar />

      <main id="main-content" tabIndex="-1" className="outline-none">
        {/* Hero Section */}
        <section className="relative pt-32 sm:pt-40 md:pt-48 pb-12 sm:pb-14 md:pb-16 overflow-hidden bg-school-blue">
          <div className="absolute inset-0 bg-school-blue-dark"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-block py-1 px-3 rounded-full bg-school-blue text-school-gold-on-blue text-xs sm:text-sm font-bold uppercase tracking-wider mb-3 sm:mb-4 border border-white/10">
              What Students Are Learning
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 tracking-tight">
              PS 155 <span className="text-school-gold-on-blue">Curriculum</span>
            </h1>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-school-gold mx-auto mb-4 sm:mb-6 rounded-full"></div>
            <p className="text-lg sm:text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto font-light leading-relaxed px-4">
              Discover what your child will learn at each grade level, from Pre-K through Grade 5. Our curriculum is designed to inspire curiosity, build strong foundations, and prepare students for success.
            </p>
          </div>
        </section>

        {/* Curriculum Highlights */}
        <section className="py-10 sm:py-11 md:py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="sr-only">Curriculum Highlights</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {highlights.map((item, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center text-center p-6 rounded-2xl bg-neutral-50 border border-neutral-100 hover:border-school-gold/30 hover:shadow-lg transition-all duration-300 group"
                >
                  <div className={`w-16 h-16 ${item.bgColor} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <item.icon className={`w-8 h-8 ${item.color}`} strokeWidth={1.5} />
                  </div>
                  <h3 className={`text-xl font-bold ${item.color} mb-2`}>{item.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Grade Level Cards */}
        <section className="py-16 sm:py-20 md:py-28 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-school-blue mb-4">
                Explore by Grade Level
              </h2>
              <p className="text-lg sm:text-xl text-neutral-600 max-w-3xl mx-auto">
                Click on a grade to learn about curriculum, partnerships, and what makes each year special.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {grades.map((grade, index) => (
                <a
                  key={index}
                  href={grade.path}
                  className="group block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-neutral-100 hover:border-school-gold/50"
                >
                  {/* Image Container */}
                  <div className="relative h-48 sm:h-56 overflow-hidden">
                    <img
                      src={grade.image}
                      alt={`${grade.name} students`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* Gradient Overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-t ${grade.color} opacity-40 group-hover:opacity-30 transition-opacity duration-300`}></div>

                    {/* Grade Badge */}
                    <div className="absolute top-4 right-4 bg-white px-4 py-2 rounded-full shadow-lg">
                      <span className="text-xs font-bold text-neutral-600 uppercase tracking-wide">{grade.ageRange}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-2xl sm:text-3xl font-bold text-school-blue mb-2 group-hover:text-school-gold-dark transition-colors">
                      {grade.name}
                    </h3>
                    <p className="text-neutral-600 mb-4 leading-relaxed">{grade.description}</p>

                    <div className="flex items-center text-school-blue group-hover:text-school-gold-dark transition-colors font-semibold">
                      <span className="text-sm">View Curriculum</span>
                      <svg className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Curriculum Standards Footer */}
        <section className="py-16 sm:py-20 bg-white border-t border-neutral-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-school-blue mb-4">
                Curriculum & Standards
              </h2>
              <div className="w-20 h-1 bg-school-gold mx-auto rounded-full mb-6"></div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {/* Pre-K Standards */}
              <div className="bg-blue-50 p-6 rounded-2xl border border-blue-100 hover:border-school-blue/50 transition-all hover:shadow-lg">
                <h3 className="text-xl font-bold text-school-blue mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-school-gold rounded-full"></span>
                  Pre-K Standards
                </h3>
                <p className="text-sm text-neutral-600 mb-4">NYS Pre-K Learning Standards</p>
                <a
                  href="https://www.nysed.gov/early-learning/prekindergarten-learning-standards"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-school-blue hover:text-school-gold-dark font-semibold text-sm transition-colors"
                >
                  View Standards
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>

              {/* ELA Standards */}
              <div className="bg-amber-50 p-6 rounded-2xl border border-amber-100 hover:border-school-gold/50 transition-all hover:shadow-lg">
                <h3 className="text-xl font-bold text-school-blue mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-school-gold rounded-full"></span>
                  ELA (K–5)
                </h3>
                <p className="text-sm text-neutral-600 mb-2">NY Next Generation ELA Standards</p>
                <p className="text-sm text-neutral-600 mb-4 font-medium">Curriculum: HMH Into Reading</p>
                <a
                  href="https://www.nysed.gov/curriculum-instruction/next-generation-ela-learning-standards"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-school-blue hover:text-school-gold-dark font-semibold text-sm transition-colors"
                >
                  View Standards
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>

              {/* Math Standards */}
              <div className="bg-emerald-50 p-6 rounded-2xl border border-emerald-100 hover:border-emerald-500/50 transition-all hover:shadow-lg md:col-span-2 lg:col-span-1">
                <h3 className="text-xl font-bold text-school-blue mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-school-gold rounded-full"></span>
                  Math (K–5)
                </h3>
                <p className="text-sm text-neutral-600 mb-2">NY Next Generation Mathematics Standards</p>
                <p className="text-sm text-neutral-600 mb-4 font-medium">Curriculum: Eureka Math</p>
                <a
                  href="https://www.nysed.gov/curriculum-instruction/next-generation-mathematics-learning-standards"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-school-blue hover:text-school-gold-dark font-semibold text-sm transition-colors"
                >
                  View Standards
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Philosophy Statement */}
            <div className="mt-12 text-center max-w-4xl mx-auto">
              <div className="bg-school-blue-dark p-8 sm:p-10 rounded-2xl shadow-xl">
                <p className="text-lg sm:text-xl text-white leading-relaxed">
                  Our instruction centers <span className="font-bold text-school-gold-on-blue">student voice</span>, <span className="font-bold text-school-gold-on-blue">culturally responsive practices</span>, and <span className="font-bold text-school-gold-on-blue">joyful learning</span>—ensuring every child grows with passion, purpose, and East Harlem pride.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Curriculum;
